import { useCallback, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ArrowUpRight, Check, Clock3, Lock, Search, ShieldCheck } from 'lucide-react';
import { usePaginatedQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type * as C from '../../../../packages/domain/src/community-admin';
import { GENRES } from '../../../../packages/domain/src/policies';
import { Button } from '../components/ui/button';
import { Modal } from '../components/common';
import { useLab } from '../lib/service';
import {
  CommunityProvider,
  useCommunity,
  type Action,
  type Filters,
  type Selection,
  type View,
} from './service';
import { fixtures } from './sample';
import './community.css';
const names: Record<View, string> = {
  overview: 'Overview',
  ideas: 'Ideas',
  people: 'People',
  voting: 'Voting & winners',
  moderation: 'Moderation',
};
const paths = {
  overview: '/community',
  ideas: '/community/ideas',
  people: '/community/people',
  voting: '/community/voting',
  moderation: '/community/moderation',
} as const;
const date = (n: number | null) =>
  n === null
    ? '—'
    : new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(n);
const time = (n: number | null) =>
  n === null
    ? 'No deadline'
    : new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }).format(n) + ' UTC';
const usd = (n: number) => `$${(n / 1e6).toFixed(2)}`;
const stageNames: Record<C.ProductionStage, string> = {
  available: 'Opportunity available',
  expired: 'Opportunity expired',
  planning: 'Planning',
  running: 'Production running',
  blocked: 'Blocked',
  review: 'Awaiting review',
  published: 'Published',
  revoked: 'Revoked',
  none: 'No production opportunity',
};
function Badge({ children, tone = '' }: { children: ReactNode; tone?: string }) {
  return <span className={`community-badge ${tone}`}>{children}</span>;
}
function PersonName({ p }: { p: C.CommunityPersonRef | null }) {
  return (
    <span>
      {p?.name ?? 'Deleted account'}
      {p?.handle && <small>@{p.handle}</small>}
    </span>
  );
}
export function Community({ page }: { page: string }) {
  const { home } = useLab();
  const view = (page.split('/')[2] || 'overview') as View;
  const search = useRouterState({ select: (s) => s.location.search }) as Record<
    string,
    string | undefined
  >;
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection | null>(null),
    [action, setAction] = useState<Action | null>(null),
    [notice, setNotice] = useState('');
  const closeDetail = useCallback(() => setSelection(null), []),
    closeAction = useCallback(() => setAction(null), []);
  const filters: Filters = {
    search: search.q ?? '',
    filter: search.filter || 'all',
    period: ['7', '30', 'all'].includes(search.period ?? '')
      ? (search.period as C.CommunityPeriod)
      : view === 'overview'
        ? '7'
        : 'all',
    genre: search.genre ?? '',
    past: search.past === 'true',
    queueState: search.qstate === 'reviewed' || search.qstate === 'all' ? search.qstate : 'pending',
  };
  function go(next: View, change: Partial<Filters> = {}) {
    const f =
      next === view
        ? { ...filters, ...change }
        : {
            search: '',
            filter: 'all',
            genre: '',
            past: false,
            queueState: 'pending' as const,
            period: 'all' as C.CommunityPeriod,
            ...change,
          };
    void navigate({
      to: paths[next],
      search: {
        q: f.search,
        filter: f.filter,
        period: f.period,
        genre: f.genre,
        past: String(f.past),
        qstate: f.queueState,
      },
    });
  }
  if (home?.access.role !== 'owner')
    return (
      <div className="community-empty">
        <Lock size={25} />
        <h1>Owner access required.</h1>
        <p>Community administration is available to the workspace owner.</p>
        <Link to="/">Return to Workspace</Link>
      </div>
    );
  return (
    <CommunityProvider view={view} filters={filters} selection={selection}>
      <section className="community-page">
        <div className="page-heading">
          <div>
            <span className="eyebrow">COMMUNITY · EDITORIAL STUDIO</span>
            <h1>
              {names[view]}
              <span>.</span>
            </h1>
            <p>
              {view === 'overview'
                ? 'The people, stories, and decisions moving your community forward.'
                : view === 'ideas'
                  ? 'Find the stories your community is rallying behind.'
                  : view === 'people'
                    ? 'From first registration to their next production.'
                    : view === 'voting'
                      ? 'Follow the vote. Keep the winning stories moving.'
                      : 'A considered review before stories reach an audience.'}
            </p>
          </div>
          <Badge>
            <ShieldCheck size={13} /> Owner only
          </Badge>
        </div>
        <Content view={view} filters={filters} go={go} select={setSelection} act={setAction} />
        {notice && (
          <div className="community-notice" role="status">
            <Check size={15} />
            {notice}
            <Button variant="ghost" size="sm" onClick={() => setNotice('')}>
              Dismiss
            </Button>
          </div>
        )}
      </section>
      {selection && !action && (
        <RecordDetail
          key={`${selection.kind}:${selection.id}`}
          selection={selection}
          onClose={closeDetail}
          select={setSelection}
          act={setAction}
        />
      )}
      {action && (
        <ActionDialog
          action={action}
          onClose={closeAction}
          done={() => {
            setAction(null);
            setNotice('Decision recorded.');
          }}
        />
      )}
    </CommunityProvider>
  );
}
type Navigation = (view: View, change?: Partial<Filters>) => void;
type Open = (s: Selection) => void;
function Content({
  view,
  filters: f,
  go,
  select,
  act,
}: {
  view: View;
  filters: Filters;
  go: Navigation;
  select: Open;
  act: (a: Action) => void;
}) {
  const {
    overview: o,
    rows,
    loading,
    more,
    loadMore,
    opportunities,
    moreOpportunities,
    loadOpportunities,
  } = useCommunity();
  if (view === 'overview')
    return o ? (
      <>
        <div className="community-toolbar">
          <span className="muted">Community pulse</span>
          <Period value={f.period} change={(period) => go(view, { period })} />
        </div>
        <div className="community-metrics">
          {[
            ['New accounts', o.counts.registrations, 'people', 'new'],
            ['Submitted ideas', o.counts.ideas, 'ideas', 'submitted'],
            ['Participating voters', o.counts.voters, 'people', 'voted'],
            ['Winners awaiting production', o.counts.awaiting, 'voting', 'awaiting'],
          ].map(([title, count, next, filter]) => (
            <button
              key={title}
              onClick={() =>
                go(next as View, {
                  filter: String(filter),
                  period: f.period,
                  past: next === 'voting',
                })
              }
            >
              <span>
                {title}
                <ArrowUpRight size={15} />
              </span>
              <strong>{count}</strong>
              <small>{f.period === 'all' ? 'All time' : `Last ${f.period} days`}</small>
            </button>
          ))}
        </div>
        <div className="community-overview-grid">
          <section className="community-panel">
            <div className="community-panel-heading">
              <div>
                <span className="eyebrow">THE CURRENT SESSION</span>
                <h2>A story takes the lead.</h2>
              </div>
              <Badge tone={o.current?.status === 'open' ? 'good' : ''}>
                {o.current?.status ?? 'Not configured'}
              </Badge>
            </div>
            <CurrentSummary o={o} />
            <LeaderList ideas={o.leaders} select={select} />
            <Button variant="outline" onClick={() => go('voting')}>
              Open current voting <ArrowUpRight size={14} />
            </Button>
          </section>
          <section className="community-panel">
            <span className="eyebrow">NEEDS ATTENTION</span>
            <h2>Keep things moving.</h2>
            <div className="community-attention">
              {[
                ['Expired opportunities', o.attention.expired, 'voting', 'expired'],
                ['Winners who haven’t started', o.attention.notStarted, 'voting', 'not-started'],
                ['Blocked production', o.attention.blocked, 'voting', 'blocked'],
                ['Pending moderation', o.attention.moderation, 'moderation', 'all'],
              ].map(([label, count, next, filter]) => (
                <button
                  key={label}
                  onClick={() =>
                    go(next as View, { filter: String(filter), past: next === 'voting' })
                  }
                >
                  <span>{label}</span>
                  <b>{count}</b>
                  <ArrowUpRight size={14} />
                </button>
              ))}
            </div>
            <p className="community-note">
              Production starts when episode 1 is submitted for generation. Planning alone does not
              meet the deadline.
            </p>
          </section>
        </div>
      </>
    ) : (
      <Loading />
    );
  return (
    <>
      {view === 'voting' && (
        <div className="community-tabs" aria-label="Voting views">
          <Button
            variant={!f.past ? 'default' : 'ghost'}
            onClick={() => go(view, { past: false, filter: 'all' })}
          >
            Current voting
          </Button>
          <Button
            variant={f.past ? 'default' : 'ghost'}
            onClick={() => go(view, { past: true, filter: 'all' })}
          >
            Past sessions
          </Button>
        </div>
      )}
      {view === 'voting' && !f.past ? (
        o ? (
          <>
            <section className="community-panel">
              <div className="community-panel-heading">
                <div>
                  <span className="eyebrow">CURRENT VOTING</span>
                  <h2>The community decides.</h2>
                </div>
                <Badge tone="good">{o.current?.status ?? 'Not configured'}</Badge>
              </div>
              <CurrentSummary o={o} />
              {o.current?.problem && <p role="alert">{o.current.problem}</p>}
              <LeaderList
                ideas={view === 'voting' ? (rows as C.CommunityIdea[]) : o.leaders}
                select={select}
              />
              {more && (
                <Button variant="outline" onClick={loadMore} disabled={loading}>
                  Load more ideas
                </Button>
              )}
              <div className="community-actions">
                <Button variant="outline" onClick={() => act({ kind: 'configure', reason: '' })}>
                  Configure next session
                </Button>
                <Button
                  variant="ghost"
                  disabled={o.current?.status !== 'open' || o.serverNow <= o.current.startsAt}
                  onClick={() => act({ kind: 'close', reason: '' })}
                >
                  Close current session
                </Button>
              </div>
            </section>
            <section className="community-panel community-next">
              <span className="eyebrow">NEXT SESSION</span>
              <h2>The next chapter.</h2>
              <p>
                {o.config
                  ? `${o.config.durationMs / 86400000} days · ${o.config.trendingWindowMs / 3600000} hour trending window · ${usd(o.config.winnerAllowance)} winner allowance`
                  : 'Configure the first voting session.'}
              </p>
              <p>
                {o.config?.nextStartsAt
                  ? `${time(o.config.nextStartsAt)} → ${time(o.config.nextEndsAt)}`
                  : 'The next session follows the existing voting schedule.'}
              </p>
              <small>
                Changes apply to the next session. The current deadline is authoritative.
              </small>
            </section>
          </>
        ) : (
          <Loading />
        )
      ) : (
        <>
          <div className="community-toolbar">
            <label className="community-search">
              <Search size={16} />
              <input
                aria-label={`Search ${names[view].toLowerCase()}`}
                placeholder={`Search ${view === 'voting' ? 'past sessions' : view}…`}
                value={f.search}
                onChange={(e) => go(view, { search: e.target.value })}
              />
            </label>
            <label className="community-filter">
              <span>{view === 'moderation' ? 'Queue' : 'Show'}</span>
              <select
                aria-label={`${names[view]} filter`}
                value={f.filter}
                onChange={(e) => go(view, { filter: e.target.value })}
              >
                {(view === 'ideas'
                  ? [
                      ['all', 'All visible ideas'],
                      ['submitted', 'All submissions'],
                      ['active', 'Active'],
                      ['winner', 'Winning'],
                      ['withdrawn', 'Withdrawn'],
                      ['archived', 'Archived'],
                      ['removed', 'Removed content'],
                    ]
                  : view === 'people'
                    ? [
                        ['all', 'All people'],
                        ['new', 'New accounts'],
                        ['submitted', 'Submitted ideas'],
                        ['no-ideas', 'No ideas'],
                        ['voted', 'Voted'],
                        ['not-voted', 'Not voted'],
                        ['incomplete', 'Incomplete onboarding'],
                        ['suspended', 'Suspended'],
                      ]
                    : view === 'voting'
                      ? [
                          ['all', 'All sessions'],
                          ['awaiting', 'Awaiting production'],
                          ['expired', 'Expired opportunities'],
                          ['not-started', 'Not started'],
                          ['planning', 'Planning'],
                          ['running', 'Production running'],
                          ['blocked', 'Blocked'],
                          ['review', 'Awaiting review'],
                          ['published', 'Published'],
                          ['no-winner', 'No winner'],
                        ]
                      : [
                          ['all', 'All queues'],
                          ['publications', 'Publications'],
                          ['comments', 'Comments'],
                          ['reports', 'Reports'],
                        ]
                ).map(([value, name]) => (
                  <option key={value} value={value}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            {view === 'ideas' && (
              <label className="community-filter">
                <span>Genre</span>
                <select
                  aria-label="Genre"
                  value={f.genre}
                  onChange={(e) => go(view, { genre: e.target.value })}
                >
                  <option value="">All genres</option>
                  {GENRES.map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </select>
              </label>
            )}
            {view === 'moderation' && (
              <label className="community-filter">
                <span>Decisions</span>
                <select
                  aria-label="Moderation decisions"
                  value={f.queueState}
                  onChange={(e) =>
                    go(view, { queueState: e.target.value as Filters['queueState'] })
                  }
                >
                  <option value="pending">Pending review</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="all">All decisions</option>
                </select>
              </label>
            )}
            {view !== 'moderation' && (
              <Period value={f.period} change={(period) => go(view, { period })} />
            )}
          </div>
          {view === 'people' && (
            <p className="community-note">
              {f.filter === 'voted'
                ? 'Period filters participation dates.'
                : 'Period filters registration dates.'}{' '}
              Verification and profile completion are shown separately. Email appears in account
              details.
            </p>
          )}
          {view === 'ideas' && (
            <p className="community-note">
              Ranking points include the existing ranking rules. Supporters are actual votes across
              sessions.
            </p>
          )}
          <div
            className={`community-table community-table-${view}`}
            aria-label={`${names[view]} records`}
          >
            <div className="community-table-head">
              {(view === 'ideas'
                ? ['Story & premise', 'Creator', 'Submitted', 'Status', 'Points / supporters']
                : view === 'people'
                  ? ['Person', 'Joined', 'Onboarding / account', 'Ideas / votes / wins']
                  : view === 'voting'
                    ? ['Session & outcome', 'Winner / creator', 'Assigned producer', 'Production']
                    : ['Content', 'Submitted by', 'Received', 'Queue / state']
              ).map((h) => (
                <span key={h}>{h}</span>
              ))}
            </div>
            {rows.map((row) =>
              view === 'ideas' ? (
                <IdeaRow key={row.id} row={row as C.CommunityIdea} select={select} />
              ) : view === 'people' ? (
                <PeopleRow key={row.id} row={row as C.CommunityPerson} select={select} />
              ) : view === 'voting' ? (
                <SessionRow key={row.id} row={row as C.CommunitySession} select={select} />
              ) : (
                <ModerationRow key={row.id} row={row as C.CommunityModeration} select={select} />
              ),
            )}
            {!rows.length && !loading && !opportunities.length && (
              <div className="community-empty">
                <Search size={24} />
                <h2>No matching records.</h2>
                <p>Try another filter or reporting period.</p>
              </div>
            )}
            {loading && <Loading />}
          </div>
          {view === 'voting' && f.past && opportunities.length > 0 && (
            <section className="community-panel community-next">
              <span className="eyebrow">OUTSIDE VOTING</span>
              <h2>Manual production assignments.</h2>
              <p className="community-note">These opportunities have no winning session.</p>
              {opportunities.map((o) => (
                <button
                  key={o.id}
                  className="community-detail-row"
                  onClick={() => select({ kind: 'opportunity', id: o.id })}
                >
                  <span>
                    {o.idea?.title ?? o.production.seriesTitle ?? 'Assigned production'}
                    <small>{o.production.producer?.name ?? 'Deleted account'}</small>
                  </span>
                  <Badge>{stageNames[o.production.stage]}</Badge>
                </button>
              ))}
            </section>
          )}
          {view === 'voting' && f.past && moreOpportunities && (
            <Button variant="outline" onClick={loadOpportunities}>
              Load more assignments
            </Button>
          )}
          <div className="community-list-footer">
            <span>
              {rows.length} loaded {rows.length === 1 ? 'record' : 'records'}
            </span>
            {more && (
              <Button variant="outline" disabled={loading} onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
}
function Loading() {
  return (
    <div className="community-empty" role="status">
      Loading community records…
    </div>
  );
}
function Period({
  value,
  change,
}: {
  value: C.CommunityPeriod;
  change: (p: C.CommunityPeriod) => void;
}) {
  return (
    <label className="community-filter">
      <span>Period</span>
      <select
        aria-label="Reporting period"
        value={value}
        onChange={(e) => change(e.target.value as C.CommunityPeriod)}
      >
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="all">All time</option>
      </select>
    </label>
  );
}
function CurrentSummary({ o }: { o: C.CommunityOverview }) {
  return (
    <div className="community-session-summary">
      <span>
        <Clock3 size={14} />{' '}
        {o.current ? `Deadline · ${time(o.current.endsAt)}` : 'No active voting session'}
      </span>
      <span>
        {o.current?.voters ?? 0} participating voters · {o.current?.votes ?? 0} actual votes ·{' '}
        {o.current?.eligible ?? 0} eligible ideas
      </span>
    </div>
  );
}
function LeaderList({ ideas, select }: { ideas: C.CommunityIdea[]; select: Open }) {
  return (
    <div className="community-leaders">
      {ideas.map((i, n) => (
        <button key={i.id} onClick={() => select({ kind: 'idea', id: i.id })}>
          <span className="community-rank">{String(n + 1).padStart(2, '0')}</span>
          <span>
            <strong>{i.title}</strong>
            <small>
              {i.genre} · {i.creator?.name ?? 'Deleted account'}
            </small>
          </span>
          <span className="community-votes">
            <strong>
              {i.points ?? '—'} <small>points</small>
            </strong>
            <small>
              {i.sessionVotes ?? '—'} session votes · {i.supporters ?? '—'} supporters
            </small>
          </span>
          <ArrowUpRight size={14} />
        </button>
      ))}
      {!ideas.length && <p className="community-note">No eligible ideas yet.</p>}
    </div>
  );
}
function IdeaRow({ row: i, select }: { row: C.CommunityIdea; select: Open }) {
  return (
    <button className="community-record" onClick={() => select({ kind: 'idea', id: i.id })}>
      <span>
        <strong>{i.title}</strong>
        <small>{i.genre}</small>
        <p>{i.premise}</p>
      </span>
      <PersonName p={i.creator} />
      <span>{date(i.createdAt)}</span>
      <span>
        <Badge tone={i.removed ? 'danger' : i.status === 'winner' ? 'good' : ''}>
          {i.removed ? 'Removed' : i.status}
        </Badge>
      </span>
      <span className="community-numbers">
        <strong>
          {i.points ?? '—'} <small>points</small>
        </strong>
        <small>{i.supporters ?? '—'} actual supporters</small>
      </span>
    </button>
  );
}
function PeopleRow({ row: p, select }: { row: C.CommunityPerson; select: Open }) {
  return (
    <button className="community-record" onClick={() => select({ kind: 'person', id: p.id })}>
      <span className="community-person">
        <span className="avatar">{p.handle ? p.name[0] : '?'}</span>
        <PersonName p={p} />
      </span>
      <span>{date(p.registeredAt)}</span>
      <span className="community-state">
        <Badge tone={!p.profileId ? 'warning' : p.status === 'suspended' ? 'danger' : 'good'}>
          {!p.profileId ? 'Incomplete onboarding' : p.status}
        </Badge>
        <small>
          {p.verified ? 'Email verified' : 'Email unverified'}
          {p.labAccess ? ' · Lab access' : ''}
        </small>
      </span>
      <span>
        <strong>
          {p.submittedIdeas} / {p.votesCast} / {p.wins}
        </strong>
        <small>ideas / votes / wins</small>
      </span>
    </button>
  );
}
function SessionRow({ row: s, select }: { row: C.CommunitySession; select: Open }) {
  return (
    <button className="community-record" onClick={() => select({ kind: 'session', id: s.id })}>
      <span>
        <strong>{date(s.endsAt)}</strong>
        <small>
          {date(s.startsAt)} → {date(s.endsAt)}
        </small>
        <Badge>{s.winner ? 'Winner selected' : 'No winner'}</Badge>
      </span>
      <span>
        <strong>{s.winner?.title ?? 'No eligible winner'}</strong>
        <small>
          {s.winner?.creator?.name ??
            (s.winner ? 'Deleted account' : 'Session ended without a winner')}
        </small>
      </span>
      {s.production.producer ? (
        <PersonName p={s.production.producer} />
      ) : (
        <span>{s.winner ? 'Unassigned' : '—'}</span>
      )}
      <span>
        <Badge
          tone={
            s.production.stage === 'expired' || s.production.stage === 'blocked'
              ? 'warning'
              : s.production.stage === 'published'
                ? 'good'
                : ''
          }
        >
          {stageNames[s.production.stage]}
        </Badge>
        <small>{s.winner ? time(s.production.deadline) : 'No production assigned'}</small>
      </span>
    </button>
  );
}
function ModerationRow({ row: m, select }: { row: C.CommunityModeration; select: Open }) {
  return (
    <button className="community-record" onClick={() => select({ kind: m.kind, id: m.id })}>
      <span>
        <strong>{m.title}</strong>
        <p>{m.text}</p>
      </span>
      <PersonName p={m.creator} />
      <span>{date(m.createdAt)}</span>
      <span>
        <Badge>{m.kind}</Badge>
        <small>{m.state}</small>
      </span>
    </button>
  );
}
function History({
  rows,
  title = 'Recorded decisions',
}: {
  rows: C.CommunityDecision[];
  title?: string;
}) {
  return (
    <section className="community-detail-section">
      <h3>{title}</h3>
      {rows.length ? (
        rows.map((d, n) => (
          <div className="community-history" key={n}>
            <strong>{d.action}</strong>
            <p>{d.reason}</p>
            <small>
              {time(d.at)} · {d.actor?.name ?? 'Workspace administrator'}
            </small>
          </div>
        ))
      ) : (
        <p className="community-note">No recorded decisions.</p>
      )}
    </section>
  );
}
function ProductionDetail({ p }: { p: C.CommunityProduction }) {
  return (
    <section className="community-detail-section">
      <h3>Linked production</h3>
      <Badge>{stageNames[p.stage]}</Badge>
      <dl>
        <dt>Assigned producer</dt>
        <dd>{p.producer?.name ?? 'No active producer'}</dd>
        <dt>Production deadline</dt>
        <dd>{p.stage === 'none' ? '—' : time(p.deadline)}</dd>
        <dt>Episode 1 submitted</dt>
        <dd>
          {p.submittedAt
            ? time(p.submittedAt)
            : p.productionStarted
              ? 'Submitted · date unavailable'
              : 'Not submitted for generation'}
        </dd>
        <dt>Production approval</dt>
        <dd>{p.productionApproved ? 'Approved private cut' : 'Not approved'}</dd>
        <dt>Public release</dt>
        <dd>{p.publicationState ?? 'Not published'}</dd>
      </dl>
      {p.problem && <p className="community-warning">{p.problem}</p>}
      {p.projectId && (
        <Link
          to="/series/$projectId"
          params={{ projectId: p.projectId }}
          className="community-link"
        >
          Open series in Workspace <ArrowUpRight size={14} />
        </Link>
      )}
    </section>
  );
}
function RecordDetail({
  onClose,
  select,
  act,
}: {
  selection: Selection;
  onClose: () => void;
  select: Open;
  act: (a: Action) => void;
}) {
  const { detail: d } = useCommunity();
  const { isDemo } = useLab();
  if (d === undefined)
    return (
      <Modal title="Opening record" onClose={onClose}>
        <Loading />
      </Modal>
    );
  if (!d)
    return (
      <Modal title="Record unavailable" onClose={onClose}>
        <p>This record was removed or the account was erased.</p>
      </Modal>
    );
  const title =
    'idea' in d
      ? d.idea.title
      : 'person' in d
        ? d.person.name
        : 'session' in d
          ? (d.session.winner?.title ?? 'Session without a winner')
          : 'opportunity' in d
            ? (d.opportunity.idea?.title ??
              d.opportunity.production.seriesTitle ??
              'Manual production assignment')
            : d.item.title;
  return (
    <Modal
      title={title}
      subtitle={isDemo ? 'Illustrative community record' : 'Private community record'}
      onClose={onClose}
      wide
    >
      <div className="community-detail">
        {'idea' in d && (
          <>
            <div className="community-actions">
              <Badge>{d.idea.removed ? 'Removed content' : d.idea.status}</Badge>
              <Badge>{d.idea.genre}</Badge>
            </div>
            <p className="community-premise">
              {d.idea.premise || 'Removed content is hidden from community views.'}
            </p>
            <dl>
              <dt>Creator</dt>
              <dd>
                {d.idea.creator ? (
                  <button
                    className="community-link"
                    onClick={() => select({ kind: 'person', id: d.idea.creator!.id })}
                  >
                    {d.idea.creator.name}
                  </button>
                ) : (
                  'Deleted account'
                )}
              </dd>
              <dt>Submitted</dt>
              <dd>{time(d.idea.createdAt)}</dd>
              <dt>Ranking points</dt>
              <dd>{d.idea.points ?? '—'}</dd>
              <dt>Actual supporters</dt>
              <dd>{d.idea.supporters ?? '—'}</dd>
              <dt>Session votes</dt>
              <dd>{d.idea.sessionVotes ?? '—'}</dd>
            </dl>
            <div className="community-actions">
              {!d.idea.removed && d.idea.status === 'active' && (
                <Button
                  variant="outline"
                  onClick={() => act({ kind: 'archive', id: d.idea.id, reason: '' })}
                >
                  Archive idea
                </Button>
              )}
              {!d.idea.removed &&
                ['active', 'winner'].includes(d.idea.status) &&
                !d.production.productionStarted && (
                  <Button
                    onClick={() =>
                      act({
                        kind: 'assign',
                        id: d.idea.id,
                        entitlementId: d.production.entitlementId ?? undefined,
                        reason: '',
                      })
                    }
                  >
                    {d.production.entitlementId ? 'Reassign production' : 'Assign production'}
                  </Button>
                )}
            </div>
            <ProductionDetail p={d.production} />
            {d.session && (
              <button
                className="community-link"
                onClick={() => select({ kind: 'session', id: d.session!.id })}
              >
                View original winning session <ArrowUpRight size={14} />
              </button>
            )}
            <History rows={d.assignments} title="Production assignment history" />
            <History rows={d.decisions} />
          </>
        )}
        {'person' in d && (
          <>
            <div className="community-actions">
              <Badge>{d.person.profileId ? 'Profile completed' : 'Incomplete onboarding'}</Badge>
              <Badge>{d.person.verified ? 'Email verified' : 'Email unverified'}</Badge>
              <Badge>{d.person.status}</Badge>
            </div>
            <dl>
              <dt>Email</dt>
              <dd>{d.email}</dd>
              <dt>Registered</dt>
              <dd>{time(d.person.registeredAt)}</dd>
              <dt>Profile completed</dt>
              <dd>{d.person.completedAt ? time(d.person.completedAt) : 'Not completed'}</dd>
              <dt>Lab access</dt>
              <dd>{d.person.labAccess ? 'Also has Lab access' : 'Consumer account'}</dd>
              <dt>Ideas / votes / wins</dt>
              <dd>
                {d.person.submittedIdeas} / {d.person.votesCast} / {d.person.wins}
              </dd>
            </dl>
            {d.person.profileId ? (
              <div className="community-actions">
                <Button
                  variant="outline"
                  onClick={() =>
                    act({
                      kind: 'restrict',
                      personId: d.person.id,
                      profileId: d.person.profileId!,
                      suspended: d.person.status !== 'suspended',
                      reason: '',
                    })
                  }
                >
                  {d.person.status === 'suspended' ? 'Restore account' : 'Suspend account'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    act({
                      kind: 'grant',
                      personId: d.person.id,
                      cap: d.allowance?.cap ?? 0,
                      reason: '',
                    })
                  }
                >
                  Manage allowance
                </Button>
              </div>
            ) : (
              <p className="community-note">
                Account and allowance controls become available after profile completion.
              </p>
            )}
            {d.allowance && (
              <p className="community-note">
                Allowance: {usd(d.allowance.cap)} cap · {usd(d.allowance.spent)} spent ·{' '}
                {usd(d.allowance.reserved)} reserved · {d.allowance.active ? 'active' : 'inactive'}
              </p>
            )}
            <section className="community-detail-section">
              <h3>Submitted ideas</h3>
              {d.ideas.map((i) => (
                <button
                  className="community-detail-row"
                  key={i.id}
                  onClick={() => select({ kind: 'idea', id: i.id })}
                >
                  <span>{i.title}</span>
                  <Badge>{i.removed ? 'Removed' : i.status}</Badge>
                </button>
              ))}
              {!d.ideas.length && <p className="community-note">No ideas submitted.</p>}
            </section>
            <section className="community-detail-section">
              <h3>Participation history</h3>
              {d.votes.map((v, n) => (
                <div className="community-history" key={n}>
                  <strong>{v.idea?.title ?? 'Idea unavailable'}</strong>
                  <small>Vote participation · {time(v.at)}</small>
                </div>
              ))}
              {!d.votes.length && <p className="community-note">No voting participation.</p>}
            </section>
            <section className="community-detail-section">
              <h3>Original wins</h3>
              {d.wins.map((w) => (
                <button
                  className="community-detail-row"
                  key={w.id}
                  onClick={() => select({ kind: 'session', id: w.id })}
                >
                  {w.winner?.title}
                  <span>{date(w.endsAt)}</span>
                </button>
              ))}
              {!d.wins.length && <p className="community-note">No winning sessions.</p>}
            </section>
            {d.opportunities.map((p, n) => (
              <ProductionDetail key={n} p={p} />
            ))}
            <History rows={d.decisions} />
          </>
        )}
        {'session' in d && !('idea' in d) && d.session && (
          <>
            <dl>
              <dt>Session dates</dt>
              <dd>
                {time(d.session.startsAt)} → {time(d.session.endsAt)}
              </dd>
              <dt>Outcome</dt>
              <dd>{d.session.winner ? 'Winner selected' : 'Ended without a winner'}</dd>
              <dt>Original winner</dt>
              <dd>{d.session.winner?.title ?? 'None'}</dd>
              <dt>Winning creator</dt>
              <dd>
                {d.session.winner?.creator ? (
                  <button
                    className="community-link"
                    onClick={() => select({ kind: 'person', id: d.session.winner!.creator!.id })}
                  >
                    {d.session.winner.creator.name}
                  </button>
                ) : d.session.winner ? (
                  'Deleted account'
                ) : (
                  '—'
                )}
              </dd>
              <dt>Winning ranking points</dt>
              <dd>{d.session.winner?.points ?? '—'}</dd>
              <dt>Actual session votes</dt>
              <dd>{d.session.winner?.sessionVotes ?? '—'}</dd>
            </dl>
            {d.session.problem && <p className="community-warning">{d.session.problem}</p>}
            {d.session.winner && (
              <>
                <ProductionDetail p={d.session.production} />
                {!d.session.winner.removed && !d.session.production.productionStarted && (
                  <Button
                    onClick={() =>
                      act({
                        kind: 'assign',
                        id: d.session.winner!.id,
                        entitlementId: d.session.production.entitlementId ?? undefined,
                        reason: '',
                      })
                    }
                  >
                    Reassign production
                  </Button>
                )}
                <p className="community-note">
                  Reassignment preserves the original winning creator and session outcome.
                </p>
              </>
            )}
            <History rows={d.assignments} title="Production assignment history" />
            <History rows={d.decisions} />
          </>
        )}
        {'opportunity' in d && (
          <>
            <Badge>Manual assignment · no voting session</Badge>
            <p className="community-note">
              This opportunity was assigned by an administrator. It does not create a historical
              winner.
            </p>
            <ProductionDetail p={d.opportunity.production} />
            {!d.opportunity.production.productionStarted && !d.opportunity.idea?.removed && (
              <Button
                onClick={() =>
                  act({
                    kind: 'assign',
                    id: d.opportunity.idea?.id,
                    projectId: d.opportunity.idea
                      ? undefined
                      : (d.opportunity.production.projectId ?? undefined),
                    entitlementId: d.opportunity.production.entitlementId ?? undefined,
                    reason: '',
                  })
                }
              >
                Reassign production
              </Button>
            )}
            <History rows={d.assignments} title="Production assignment history" />
            <History rows={d.decisions} />
          </>
        )}
        {'item' in d && (
          <ModerationDetail key={d.item.id} item={d.item} decisions={d.decisions} act={act} />
        )}
      </div>
    </Modal>
  );
}
function ModerationDetail({
  item: m,
  decisions,
  act,
}: {
  item: C.CommunityModeration;
  decisions: C.CommunityDecision[];
  act: (a: Action) => void;
}) {
  const { preview } = useCommunity();
  const [src, setSrc] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  async function refresh() {
    if (!m.masterId) return;
    setError('');
    setSrc('');
    setBusy(true);
    try {
      setSrc(await preview(m.masterId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Badge>
        {m.kind} · {m.state}
      </Badge>
      <p className="community-premise">{m.text}</p>
      <dl>
        <dt>Submitted by</dt>
        <dd>{m.creator?.name ?? 'Deleted account'}</dd>
        <dt>Received</dt>
        <dd>{time(m.createdAt)}</dd>
        {m.version !== null && (
          <>
            <dt>Exact publication master</dt>
            <dd>Version {m.version}</dd>
          </>
        )}
      </dl>
      {m.masterId && (
        <section className="community-detail-section">
          <h3>Private master preview</h3>
          <p className="community-note">This review opens the master attached to this record.</p>
          {src && (
            <video
              key={src}
              src={src}
              controls
              playsInline
              preload="metadata"
              aria-label="Private reviewed master"
              onError={() => setError('The video could not load. Refresh access and try again.')}
            />
          )}
          <Button variant="outline" disabled={busy} onClick={() => void refresh()}>
            {busy ? 'Opening preview…' : src ? 'Refresh video access' : 'Open private preview'}
          </Button>
          {error && (
            <p role="alert" className="community-warning">
              {error}
            </p>
          )}
        </section>
      )}
      {['pending', 'open'].includes(m.state) && (
        <div className="community-actions">
          {m.kind === 'reports' ? (
            <>
              <Button
                onClick={() =>
                  act({ kind: 'resolve', id: m.id, moderationKind: m.kind, reason: '' })
                }
              >
                Resolve without removal
              </Button>
              <Button
                variant="outline"
                disabled={!m.targetKind}
                onClick={() =>
                  act({
                    kind: 'remove',
                    id: m.id,
                    moderationKind: m.kind,
                    suspended: m.targetKind === 'person',
                    reason: '',
                  })
                }
              >
                {m.targetKind === 'person' ? 'Suspend reported account' : 'Remove reported content'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() =>
                  act({ kind: 'approve', id: m.id, moderationKind: m.kind, reason: '' })
                }
              >
                Approve {m.kind === 'publications' ? 'publication' : 'comment'}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  act({ kind: 'reject', id: m.id, moderationKind: m.kind, reason: '' })
                }
              >
                Reject {m.kind === 'publications' ? 'publication' : 'comment'}
              </Button>
            </>
          )}
        </div>
      )}
      <History rows={decisions} />
    </>
  );
}
function ActionDialog({
  action: a,
  onClose,
  done,
}: {
  action: Action;
  onClose: () => void;
  done: () => void;
}) {
  const { run, overview: o } = useCommunity();
  const [reason, setReason] = useState(''),
    [producer, setProducer] = useState(''),
    [deadline, setDeadline] = useState(''),
    [noExpiry, setNoExpiry] = useState(false),
    [cap, setCap] = useState(String((a.cap ?? 0) / 1e6)),
    [duration, setDuration] = useState(String((o?.config?.durationMs ?? 7 * 86400000) / 86400000)),
    [trending, setTrending] = useState(String((o?.config?.trendingWindowMs ?? 86400000) / 3600000)),
    [allowance, setAllowance] = useState(String((o?.config?.winnerAllowance ?? 2000000) / 1e6)),
    [start, setStart] = useState(''),
    [end, setEnd] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const title =
    a.kind === 'archive'
      ? 'Archive idea'
      : a.kind === 'assign'
        ? 'Assign production'
        : a.kind === 'configure'
          ? 'Configure next session'
          : a.kind === 'close'
            ? 'Close current session'
            : a.kind === 'restrict'
              ? a.suspended
                ? 'Suspend account'
                : 'Restore account'
              : a.kind === 'grant'
                ? 'Manage allowance'
                : a.kind === 'remove'
                  ? a.suspended
                    ? 'Suspend reported account'
                    : 'Remove reported content'
                  : a.kind === 'resolve'
                    ? 'Resolve report'
                    : a.kind === 'approve'
                      ? 'Approve ' +
                        (a.moderationKind === 'publications' ? 'publication' : 'comment')
                      : 'Reject ' +
                        (a.moderationKind === 'publications' ? 'publication' : 'comment');
  const reasonMin = a.moderationKind === 'publications' ? 10 : 5;
  const utc = (s: string) => new Date(s + 'Z').getTime();
  async function submit() {
    setError('');
    const next = { ...a, reason: reason.trim() };
    if (next.reason.length < reasonMin) {
      setError(`Record a reason of at least ${reasonMin} characters.`);
      return;
    }
    if (a.kind === 'assign') {
      if (!producer || (!noExpiry && (!deadline || utc(deadline) <= Date.now()))) {
        setError('Choose an active producer and a future UTC deadline, or no expiry.');
        return;
      }
      next.personId = producer;
      next.expiresAt = noExpiry ? undefined : utc(deadline);
    }
    if (a.kind === 'grant') {
      if (!/^\d+(\.\d{1,6})?$/.test(cap) || Number(cap) > 100) {
        setError('Enter an allowance from $0 to $100, with at most six decimal places.');
        return;
      }
      next.cap = Math.round(Number(cap) * 1e6);
    }
    if (a.kind === 'configure') {
      next.durationMs = Math.round(Number(duration) * 86400000);
      next.trendingWindowMs = Math.round(Number(trending) * 3600000);
      next.winnerAllowance = Math.round(Number(allowance) * 1e6);
      if (
        ![next.durationMs, next.trendingWindowMs, next.winnerAllowance].every(
          (n) => Number.isSafeInteger(n) && n > 0,
        ) ||
        next.durationMs < 60000
      ) {
        setError('Enter a positive duration, trending window, and winner allowance.');
        return;
      }
      if (start || end) {
        if (
          !start ||
          !end ||
          utc(start) < Date.now() ||
          utc(end) <= utc(start) ||
          (o?.current && utc(start) < o.current.endsAt)
        ) {
          setError(
            'Choose future UTC times after the current session, with the end after the start.',
          );
          return;
        }
        next.startsAt = utc(start);
        next.endsAt = utc(end);
      }
    }
    setBusy(true);
    try {
      await run(next);
      done();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to complete this action.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={title}
      onClose={busy ? () => {} : onClose}
      subtitle="Your reason will be recorded with this decision."
    >
      <form
        className="community-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {a.kind === 'archive' && (
          <p>
            Archiving uses the existing removal behavior. The idea leaves voting and its content
            becomes hidden.
          </p>
        )}
        {a.kind === 'assign' && (
          <>
            <p>
              Assign an unstarted opportunity. The original winning creator and session remain in
              history.
            </p>
            <ProducerPicker value={producer} change={setProducer} />
            <label>
              Production deadline (UTC)
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                disabled={noExpiry}
                required={!noExpiry}
              />
            </label>
            <label className="community-checkbox">
              <input
                type="checkbox"
                checked={noExpiry}
                onChange={(e) => setNoExpiry(e.target.checked)}
              />{' '}
              No expiry
            </label>
          </>
        )}
        {a.kind === 'grant' && (
          <label>
            Total allowance cap (USD)
            <input
              type="number"
              min="0"
              max="100"
              step="0.000001"
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              required
            />
          </label>
        )}
        {a.kind === 'configure' && (
          <>
            <p>
              These values apply to the next session. Current voting keeps its existing deadline.
            </p>
            <label>
              Session duration (days)
              <input
                type="number"
                min="0.001"
                step="any"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
              />
            </label>
            <label>
              Trending window (hours)
              <input
                type="number"
                min="0.001"
                step="any"
                value={trending}
                onChange={(e) => setTrending(e.target.value)}
                required
              />
            </label>
            <label>
              Winner allowance (USD)
              <input
                type="number"
                min="0.000001"
                step="0.000001"
                value={allowance}
                onChange={(e) => setAllowance(e.target.value)}
                required
              />
            </label>
            <label>
              Next start (UTC, optional)
              <input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label>
              Next end (UTC, optional)
              <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </>
        )}
        {a.kind === 'close' && (
          <p>
            This ends voting now and runs the existing selection process. A winner is selected only
            if eligible ideas remain.
          </p>
        )}
        {a.kind === 'remove' && (
          <p>
            {a.suspended
              ? 'The reported account will be suspended and this report resolved.'
              : 'The reported content will be removed and this report resolved.'}
          </p>
        )}
        <label>
          Recorded reason
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            minLength={reasonMin}
            maxLength={1000}
            required
            rows={4}
          />
        </label>
        {error && (
          <p role="alert" className="community-warning">
            {error}
          </p>
        )}
        <div className="community-actions">
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || reason.trim().length < reasonMin}>
            {busy ? 'Recording…' : title}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
function ProducerPicker({ value, change }: { value: string; change: (id: string) => void }) {
  const { isDemo } = useLab();
  return isDemo ? (
    <ProducerOptions
      people={fixtures().people.filter((p) => p.status === 'active' && p.profileId)}
      value={value}
      change={change}
    />
  ) : (
    <LiveProducerPicker value={value} change={change} />
  );
}
function LiveProducerPicker({ value, change }: { value: string; change: (id: string) => void }) {
  const { results, status, loadMore } = usePaginatedQuery(
    api.communityAdmin.people,
    { filter: 'producers', period: 'all' },
    { initialNumItems: 20 },
  );
  return (
    <>
      <ProducerOptions people={results} value={value} change={change} />
      {status === 'CanLoadMore' && (
        <Button type="button" variant="ghost" onClick={() => loadMore(20)}>
          Load more producers
        </Button>
      )}
    </>
  );
}
function ProducerOptions({
  people,
  value,
  change,
}: {
  people: C.CommunityPerson[];
  value: string;
  change: (id: string) => void;
}) {
  return (
    <label>
      Assigned producer
      <select value={value} onChange={(e) => change(e.target.value)} required>
        <option value="">Choose an active account</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} · @{p.handle}
          </option>
        ))}
      </select>
    </label>
  );
}
